import React, { useState, useEffect, useRef, useMemo } from "react";
import { X, ChevronLeft } from "lucide-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import StudentWellnessData from "./StudentWellnessData";
import Alerts from "./Alerts";
import AlertsList from "./AlertsList";
import StudentList from "./StudentList";
import { handleStudent } from "@/screens/StudentData/handlers/studentHandler";
import { handleMultiEmotion } from "@/screens/StudentData/handlers/multiEmotionHandler";
import { findStudentClass } from "@/screens/Dashboards/utils/studentSupportsUtils";

dayjs.extend(relativeTime);

const StudentDetailModal = ({
  selectedStudent,
  state,
  updateState,
  onClose,
  pinnedStudents,
  formattedEmotions,
  allEmotions,
  userId,
  user,
  filterCount,
  alerts,
  togglePinnedStudent,
  filteredAlerts,
}) => {
  const [tab, setTab] = useState("wellness");
  const [showAlertsPopup, setShowAlertsPopup] = useState(false);
  const [studentData, setStudentData] = useState(state);
  const [isLgUp, setIsLgUp] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(min-width: 1024px)").matches;
  });
  const popupRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;

    const mediaQueryList = window.matchMedia("(min-width: 1024px)");
    const handleChange = (event) => setIsLgUp(event.matches);

    setIsLgUp(mediaQueryList.matches);

    if (mediaQueryList.addEventListener) {
      mediaQueryList.addEventListener("change", handleChange);
      return () => mediaQueryList.removeEventListener("change", handleChange);
    }

    mediaQueryList.addListener(handleChange);
    return () => mediaQueryList.removeListener(handleChange);
  }, []);

  useEffect(() => {
    const updateStudentData = async () => {
      let newStudentData = { ...state };

      if (selectedStudent) {
        const { district, school, className } = await findStudentClass(
          state.districts,
          selectedStudent
        );

        newStudentData = {
          ...state,
          chosenDistrict: district,
          chosenSchool: school,
          chosenClass: className,
          chosenStudent: selectedStudent,
          currentStudents: [selectedStudent],
        };

        newStudentData = {
          ...newStudentData,
          ...handleStudent(newStudentData, selectedStudent),
        };

        newStudentData = {
          ...newStudentData,
          ...handleMultiEmotion(newStudentData, state.multiEmotion.emotions),
        };
      } else {
        newStudentData = {
          ...state,
          ...handleMultiEmotion(newStudentData, state.multiEmotion.emotions),
        };

        if (state.chosenStudent && state.chosenStudent !== "All Students") {
          newStudentData = {
            ...newStudentData,
            ...handleStudent(newStudentData, state.chosenStudent),
            currentStudents: [state.chosenStudent],
          };
        }
      }

      setStudentData(newStudentData);
    };

    updateStudentData();
  }, [state, selectedStudent]);

  const emotionPercents = useMemo(() => {
    const emotionData = studentData.counts || [];
    const totalCurrent = emotionData.reduce(
      (sum, d) => sum + (d.current || 0),
      0
    );
    const totalCompare = emotionData.reduce(
      (sum, d) => sum + (d.compare || 0),
      0
    );

    return emotionData.map((d) => {
      const currentPercent =
        totalCurrent > 0 ? Math.round((d.current / totalCurrent) * 100) : 0;
      const comparePercent =
        totalCompare > 0 ? Math.round((d.compare / totalCompare) * 100) : 0;

      return {
        ...d,
        currentPercent,
        comparePercent,
        change: currentPercent - comparePercent,
      };
    });
  }, [studentData.counts]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setShowAlertsPopup(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const name =
    studentData.studentEmailToNameMap[studentData.chosenStudent] ||
    studentData.chosenStudent;
  // Ensure only the chosen student is passed
  const modalState = {
    ...state,
    chosenStudent: selectedStudent || state.chosenStudent,
    studentSupportData: studentData.studentSupportData || [],
    filteredDataPoints: studentData.filteredDataPoints || [],
    contextFilteredDataPoints: state.contextFilteredDataPoints || [],
    currentStudents: selectedStudent
      ? [selectedStudent]
      : state.chosenStudent && state.chosenStudent !== "All Students"
      ? [state.chosenStudent]
      : state.currentStudents || [],
  };

  return (
    <div className="relative w-full lg:w-2/3 sm:shadow sm:rounded sm:bg-white  mt-14 sm:mt-0">
      {!isLgUp && (
        <div className="text-start mb-2 sm:mb-0 sm:px-5 sm:pt-5">
          <button
            onClick={onClose}
            data-onboarding-id="student-supports-modal-x"
            className="text-sm sm:text-base font-bold text-[#005BE2] flex items-center gap-1"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to students
          </button>
        </div>
      )}
      <div
        className="sm:sticky top-0 z-10 bg-white sm:px-5 sm:pb-5 sm:pt-5 lg:pt-8 2xl:px-6 2xl:pb-6"
        data-onboarding-id="student-supports-selected-student"
      >
        {isLgUp && (
          <div className="absolute top-2 right-2">
            <X
              onClick={onClose}
              className="w-5 h-5 text-black cursor-pointer"
              data-onboarding-id="student-supports-modal-x"
            />
          </div>
        )}
        {selectedStudent && (
          <StudentList
            state={modalState}
            updateState={updateState}
            selectedStudent={selectedStudent}
            formattedEmotions={formattedEmotions}
            allEmotions={allEmotions}
            filterCount={filterCount}
            alerts={alerts}
            pinnedStudents={pinnedStudents}
            userId={userId}
            togglePinnedStudent={togglePinnedStudent}
            onStudentClick={() => {}}
            enableAutoEmotionUpdate={true}
            isModal={true} // Ensure only chosen student is shown
            isModalOpen={true}
          />
        )}
      </div>

      {/* Tabs */}
      <div className="flex justify-between items-center my-3 sm:my-0 sm:mb-6 sm:mx-5 2xl:mx-6 border-b border-[#DDDDDD]">
        <div className="flex gap-3">
          <button
            className={`text-base px-2 py-1 sm:px-3 rounded ${
              tab === "wellness"
                ? "bg-[#F3F8FD] border-b-2 border-[#005BE2] font-bold"
                : "opacity-80 hover:opacity-100 text-400"
            }`}
            onClick={() => setTab("wellness")}
          >
            Wellness Data
          </button>
          <div
            className={`text-base gap-1 sm:gap-2 flex flex-row items-center px-2 py-1 sm:px-3 rounded ${
              tab === "alerts"
                ? "bg-[#F3F8FD] border-b-2 border-[#005BE2] font-bold"
                : "opacity-80 hover:opacity-100 text-400"
            }`}
          >
            <button
              onClick={() => setTab("alerts")}
              data-onboarding-id="student-supports-alerts"
            >
              Alerts
            </button>
            {filteredAlerts.length > 0 && selectedStudent && (
              <div
                onClick={() => setShowAlertsPopup(true)}
                className="cursor-pointer flex items-center justify-center ml-1 w-4 h-4 rounded-full text-[11px] text-white bg-[#6366F1]"
                title="Show recent alerts"
              >
                <span className="flex items-center justify-center">
                  {filteredAlerts.length}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tab content */}
      {tab === "wellness" ? (
        <div className="sm:px-5 2xl:px-6 pb-6">
          <StudentWellnessData
            email={selectedStudent || state.chosenStudent}
            name={name}
            state={studentData}
            updateState={updateState}
            selectedEmotions={allEmotions}
            emotionPercents={emotionPercents}
            user={user}
          />
        </div>
      ) : (
        <div className="relative sm:px-5 2xl:px-6 pb-6">
          {(selectedStudent || state.chosenStudent) && (
            <Alerts
              email={selectedStudent || state.chosenStudent}
              name={name}
              filteredAlerts={filteredAlerts}
              state={studentData}
              updateState={updateState}
              user={user}
            />
          )}
          {showAlertsPopup && (
            <div
              ref={popupRef}
              className="absolute sm:left-2 top-0 bg-white border border-gray-300 shadow"
            >
              <AlertsList
                filteredAlerts={filteredAlerts}
                studentEmailToNameMap={state.studentEmailToNameMap}
                chosenClass={state.chosenClass}
                state={studentData}
                updateState={updateState}
                formattedEmotions={formattedEmotions}
                user={user}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StudentDetailModal;
